import React, { useEffect, useState, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import { CSSTransition } from "react-transition-group";
import { FlowScreenWrapper } from "../shared/FlowScreenWrapper";
import { useAuthContext } from "../../../AuthContextWrapper";
import { FinancialConvoContext } from "FinancialConvoContextWrapper";
import { FinancialConvo } from "models/FinancialConvo";
import { ReactComponent as LockIcon } from "../../assets/lock.svg";
import clipboardImg1x from "../../assets/ani-clipboard-1x.png";
import clipboardImg15x from "../../assets/ani-clipboard-1.5x.png";
import clipboardImg2x from "../../assets/ani-clipboard-2x.png";
import clipboardImg3x from "../../assets/ani-clipboard-3x.png";

import "./animations.css";
import { AtticusMySQL } from "apis/AtticusMySQL";
import { Subscription } from "models/Subscription";

export enum Outcomes {
  paymentRequired = "Payment Required",
  submission = "Submission",
}

const animatedElement = {
  position: "absolute",
  textAlign: "center",
  mx: "auto",
  left: 0,
  right: 0,
} as const;

const animatedIcon = {
  height: "120px",
};

// Animation use react-transition-group library
//  https://reactcommunity.org/react-transition-group/css-transition
//  https://www.npmjs.com/package/react-transition-group
export const AnimateYourAnswersScreen = (): JSX.Element => {
  const navigate = useNavigate();
  const { flow } = useAuthContext()
  const {
    latestFinancialConvo,
    setLatestFinancialConvo,
    setSuccessfulSubmission,
  } = useContext(FinancialConvoContext);
  const { screenName } = useParams();
  const { activeEstate, user } = useAuthContext()
  const currentScreen = flow?.getCurrentFlowScreen(screenName);

  // animation trackers
  const [scene1Icon, setScene1Icon] = useState(false);
  const [scene1Title, setScene1Title] = useState(false);
  const [scene2Icon, setScene2Icon] = useState(false);
  const [scene2Title, setScene2Title] = useState(false);

  const [subscription, setSubscription] = useState<Subscription>();

  const checkPaymentPlan = async () => {
    if(!user?.id) {
      return
    }
    const plan = await AtticusMySQL.getSubscription(user?.id);
    setSubscription(plan);
  };

  const submitRequest = async () => {
    const financialConvo = latestFinancialConvo || new FinancialConvo();
    try {
      if (!activeEstate?.id || !user?.id) {
        throw "No estate or user ID";
      }

      const ret = await financialConvo.submitRequest(activeEstate.id, user.id);
      // Wait and check to see if the financial convo was actually submitted before continuing
      let success = false;

      for (let i = 0; i < 10; i++) {
        success = await financialConvo.checkRequestSubmit(activeEstate.id, user.id);
        if (success) break;
        await new Promise((r) => setTimeout(r, 1000));
      }

      if (success) {
        setLatestFinancialConvo && setLatestFinancialConvo(financialConvo);
        setSuccessfulSubmission && setSuccessfulSubmission(!!ret);
      } else {
        throw "Timed out";
      }
    } catch (error) {
      setSuccessfulSubmission && setSuccessfulSubmission(false);
    }
  };

  const handleComplete = async (outcome: string): Promise<boolean> => {
    if (!currentScreen) throw Error("couldn't determine currentScreen");

    flow?.navigateToNext(currentScreen, outcome, navigate);
    return true;
  };

  useEffect(() => {
    setTimeout(() => {
      // start the animation sequence
      setScene1Icon(true);
    }, 500);
    1;
  }, []);

  // use 0.5 of screens larger than 1000, 0.8 for screens smaller than that
  const stageHeight =
    window.innerHeight -
    window.innerHeight * (window.innerHeight > 1000 ? 0.5 : 0.2);

  return currentScreen ? (
    <FlowScreenWrapper hideSectionHeading={true} centerContent={true}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            position: "relative",
            height: stageHeight,
            width: "100%",
            textAlign: "center",
          }}
        >
          {/* payment icon */}
          <CSSTransition
            in={scene1Icon}
            timeout={1000}
            classNames="icon"
            unmountOnExit
            onEntered={() => {
              // slide in the title
              setScene1Title(true);
            }}
          >
            <Box sx={{ ...animatedElement, ...animatedIcon }}>
              <LockIcon />
            </Box>
          </CSSTransition>
          {/* clipboard icon title */}
          <CSSTransition
            in={scene1Title}
            timeout={500}
            classNames="title"
            unmountOnExit
            onEntered={async () => {
              await checkPaymentPlan();
              setScene1Icon(false);
              setScene1Title(false);
            }}
            onExited={() => {
              // slide in the next icon
              if (subscription && !subscription.isActive) {
                handleComplete(Outcomes.paymentRequired);
              }
              setScene2Icon(true);
            }}
          >
            <Box sx={animatedElement}>
              <Typography variant="h3">Checking Your Atticus Plan</Typography>
            </Box>
          </CSSTransition>
          {/* ----------- next group of elements -----------  */}
          {/* clipboard icon */}
          <CSSTransition
            in={scene2Icon}
            timeout={1000}
            classNames="icon"
            unmountOnExit
            onEntered={() => {
              // slide in the title
              setScene2Title(true);
            }}
          >
            <Box sx={{ ...animatedElement, ...animatedIcon }}>
              <img
                src={clipboardImg1x}
                srcSet={`${clipboardImg15x} 1.5x, ${clipboardImg2x} 2x, ${clipboardImg3x} 3x`}
                alt="clipboard"
              />
            </Box>
          </CSSTransition>
          {/* clipboard icon title */}
          <CSSTransition
            in={scene2Title}
            timeout={500}
            classNames="title"
            unmountOnExit
            onEntered={async () => {
              await submitRequest();
              setScene2Icon(false);
              setScene2Title(false);
            }}
            onExited={() => {
              handleComplete(Outcomes.submission);
            }}
          >
            <Box sx={animatedElement}>
              <Typography variant="h3">
                Compiling your answers and estate profile...
              </Typography>
            </Box>
          </CSSTransition>
        </Box>
      </Box>
    </FlowScreenWrapper>
  ) : (
    <></>
  );
};
